// src/modules/restaurant/pages/running-orders/components/OrderCard.tsx
import React from "react";
import { Edit2, CreditCard, Users, Calendar, Sparkles, Table, Printer } from "lucide-react";
import { Order } from "src/shared/services/interfacev2";
import { splitDateTime } from "src/shared/components/dateTime";
import { useUser } from "src/shared/context/UserContext";
import { useDateTimeFormatter } from "src/shared/configurations/hooks/useDateTimeFormatter";

interface Props {
  order: Order;
  themeTextColor: string;
  themeBorderColor: string;
  getLightBgClass: () => string;
  getBgClass: () => string;
  handleEdit: (id: string) => void;
  handlePay: (orderId: string) => void;
  onPrint: (order: Order) => void;
}

const OrderCard: React.FC<Props> = ({ order, themeTextColor, handleEdit, handlePay, getBgClass, onPrint }) => {
  const { userConfigration } = useUser();
  const toBoolean = (value: any) => value === true || value === "true";
  const deliveryFeeEnabled = toBoolean(userConfigration?.deliveryFeeEnabled);
  const parcelFeeEnabled = toBoolean(userConfigration?.parcelFeeEnabled);
  const { formatDate, formatTime } = useDateTimeFormatter();
  
  const statusConfig = (status: string) => {
    const s = status?.toLowerCase();
    switch (s) {
      case "completed":
        return {
          class: "bg-emerald-50/80 text-emerald-700 border border-emerald-200/60",
          dot: "bg-emerald-500",
          text: "Paid & Completed",
          gradient: "from-emerald-500 to-emerald-600",
        };
      case "pending":
        return {
          class: "bg-amber-50/80 text-amber-700 border border-amber-200/60",
          dot: "bg-amber-500",
          text: "Pending Payment",
          gradient: "from-amber-500 to-amber-600",
        };
      case "cancelled":
        return {
          class: "bg-rose-50/80 text-rose-700 border border-rose-200/60",
          dot: "bg-rose-500",
          text: "Cancelled",
          gradient: "from-rose-500 to-rose-600",
        };
      default:
        return {
          class: "bg-slate-50/80 text-slate-700 border border-slate-200/60",
          dot: "bg-slate-500",
          text: "Draft",
          gradient: "from-slate-500 to-slate-600",
        };
    }
  };
  
  const tnx = order.orders[0];
  const orderedOn = order.orderDetails?.[0]?.orderedOn?.$date ?? "";
  const { date: orderDate, time: orderTime } = splitDateTime(orderedOn);
  const statusDetails = statusConfig(tnx.status);
  const isDisabled = ["completed", "cancelled"].includes(tnx.status?.toLowerCase() || "");
  
  const truncateText = (text: any, length: number) => {
    const safeText = text ? String(text) : "";
    return safeText.length > length ? safeText.slice(0, length) + "..." : safeText;
  };
  
  const getSafeId = (obj: any) => {
    if (!obj) return "";
    if (typeof obj === "string") return obj;
    if ("$oid" in obj) return obj.$oid;
    return "";
  };

  const products = order.orderDetails?.[0]?.products ?? [];

  const getTableLabel = () => {
    return order.orderDetails[0]?.orderType === "Delivery" ? "Delivery" : "Table No";
  };

  const getTableOrDelivery = () => {
    return order.orderDetails[0].orderType === "Delivery" ? "Delivery" : order.orders[0]?.tableNumber || "-";
  };

  // Add print handler
  const handlePrint = () => { onPrint(order)};

  return (
    <div className="group relative">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-700/8 to-purple-700/8 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-700 ease-out" />
      <div
        className={`relative bg-white/95 backdrop-blur-sm border border-gray-400 rounded-3xl p-4 shadow-2xl shadow-gray-300/30 hover:shadow-3xl hover:shadow-gray-300/40 
                    transition-all duration-500 ease-out flex flex-col transform hover:-translate-y-1 group-hover:border-gray-200/80 overflow-hidden`}
      >
        <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(0,0,0,0.02)_50%,transparent_75%)] bg-[length:4px_4px] opacity-20" />

        <div className="flex items-start justify-between mb-1 relative">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Sparkles className={`w-4 h-4 ${themeTextColor}`} />
              <p className={`text-xs font-medium uppercase tracking-widest ${themeTextColor}`}>Order ID</p>
            </div>
            <h2 className={`text-2xl font-black text-gray-900 tracking-tight group-hover:${themeTextColor} transition-colors duration-300`}>{tnx.orderId}</h2>
          </div>

          <div className={`flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-semibold tracking-tight backdrop-blur-sm flex-shrink-0 ${statusDetails.class}`}>
            <div className={`w-2 h-2 rounded-full ${statusDetails.dot} animate-pulse shadow-lg shadow-${statusDetails.dot}/30`}></div>
            <span className="truncate">{statusDetails.text}</span>
          </div>
        </div>

        <div className="flex flex-col gap-2 text-sm text-gray-600 mb-4 pb-2 border-b border-gray-100/80">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-xl bg-gray-50/80 border border-gray-100 ${themeTextColor}`}>
              <Users className="w-4 h-4" />
            </div>
            <div>
              <p className="text-xs text-gray-500/80 font-medium">Customer</p>
              <p className="font-semibold text-gray-900 truncate">{order.customerDetails?.firstName || order.customerDetails?.lastName ? [order.customerDetails.firstName, order.customerDetails.middleName, order.customerDetails.lastName]
                  .filter(Boolean).join(" ") : "Walk-in Customer"}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-2">
              <div className={`p-1.5 rounded-xl bg-gray-50/80 border border-gray-100 ${themeTextColor}`}>
                <Table className="w-4 h-4" />
              </div>
              <div>
                <p className='text-xs text-gray-500/80 font-medium'>{getTableLabel()}</p>
                <p className='font-semibold text-gray-900'>{getTableOrDelivery()}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 justify-end text-left">
              <div className={`p-1.5 rounded-xl bg-gray-50/80 border border-gray-100 ${themeTextColor}`}>
                <Calendar className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xs text-gray-500/80 font-medium">Date & Time</p>
                <p className="font-semibold text-gray-900">
                  {formatDate(orderDate)} • {formatTime(orderTime)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative mb-1 -mt-2.5 flex-1">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-600/80">Items ({order.orderDetails?.[0]?.products?.length || 0})</p>
          </div>

          <div className={`flex justify-between text-xs font-semibold text-gray-600 px-2 mb-1 ${products.length > 3 ? "pr-4" : ""}`}>
            <span className="flex-1">Product Name</span>
            <span className="w-[45px] text-right">Qty</span>
            <span className="w-[70px] text-right">Amount</span>
            <span className="w-[70px] text-right">Total</span>
          </div>

          <div className="thin-scrollbar space-y-1 min-h-[125px] max-h-[125px] overflow-y-auto pr-0.5">
            {order.orderDetails?.[0]?.products?.map((p, i) => (
              <div key={i} className="flex justify-between items-center px-2 py-1 rounded-lg bg-gray-100 border border-gray-200">
                <span className="flex-1 text-sm font-medium text-gray-800 truncate">{truncateText(p.name, 35)}</span>
                <span className={`w-[45px] text-right text-sm font-semibold ${themeTextColor}`}>{p.quantity}</span>
                <span className="w-[70px] text-right text-sm font-bold">{Number(p.unitPrice).toFixed(2)}</span>
                <span className="w-[70px] text-right text-sm font-bold">{Number(p.quantity * p.unitPrice).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2.5 mb-3 pt-2 border-t border-gray-100/80 relative z-10">
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-600 font-medium">Subtotal</span>
            <span className="font-semibold text-gray-900">{tnx.orderAmount.toFixed(2)}</span>
          </div>
          {deliveryFeeEnabled && (
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600 font-medium">Delivery Fee</span>
              <span className="font-semibold text-gray-900">+ {Number(order.orders?.[0]?.deliveryFee || 0).toFixed(2)}</span>
            </div>
          )}
          {parcelFeeEnabled && (
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600 font-medium">Parcel Fee</span>
              <span className="font-semibold text-gray-900">+ {Number(order.orders?.[0]?.parcelFee || 0).toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-600 font-medium">Discount</span>
            <span className="font-semibold text-rose-600">- {Number(tnx.discount || 0).toFixed(2)}</span>
          </div>

          <div className="flex justify-between items-center pt-1 border-t border-gray-200/60">
            <span className="font-bold text-gray-900 tracking-tight">Total Due</span>
            <div className="text-right">
              <span className={`font-black ${themeTextColor} tracking-tight bg-gradient-to-r ${statusDetails.gradient} bg-clip-text text-transparent`}>{tnx.finalAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 relative">
          <button
            onClick={handlePrint}
            className={`flex items-center gap-2 px-4 py-3 rounded-2xl text-white font-bold transition-all duration-300 shadow-sm hover:shadow-lg hover:scale-105 backdrop-blur-sm bg-gradient-to-r ${getBgClass()}`}
          >
            <Printer className="w-5 h-5" />
            <span className="text-sm font-bold relative tracking-tight">Print</span>
          </button>

          <button
            onClick={() => handleEdit(getSafeId(order.orderId))}
            disabled={isDisabled}
            className={`flex items-center gap-2 px-4 py-3 rounded-2xl bg-white text-gray-700 border border-gray-400/80 hover:border-gray-400 hover:bg-gray-50 hover:shadow-lg disabled:opacity-30 disabled:cursor-not-allowed disabled:grayscale transition-all duration-300 shadow-sm hover:scale-105 backdrop-blur-sm`}
          >
            <Edit2 className="w-5 h-5" />
            <span className="text-sm font-bold relative tracking-tight">Edit Order</span>
          </button>

          <button
            onClick={() => handlePay(tnx.orderId)}
            disabled={isDisabled}
            className={`flex-1 bg-gradient-to-r ${getBgClass()} text-white font-bold py-3 px-4 rounded-2xl transition-all duration-300 shadow-xl shadow-gray-400/30 hover:shadow-2xl hover:shadow-gray-500/40 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:grayscale flex items-center justify-center gap-3 group/button relative overflow-hidden`}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform translate-x-[-100%] group-hover/button:translate-x-[100%] transition-transform duration-1000" />
            <CreditCard className="w-5 h-5 relative" />
            <span className="relative z-10 tracking-tight">{isDisabled ? "Payment Completed" : "Pay Bill"}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderCard;